import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  Plus, Search, Lock, ChevronRight, HardHat, AlertTriangle, TrendingDown, Settings as SettingsIcon, Users,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { gbp, ukDate } from "../lib/format";
import { PageHeader, Card, PrimaryButton, EmptyState } from "../components/UI";
import { LabourRatesBulkTab } from "./settings/LabourRatesBulkTab";
import { SubcontractorDirectory } from "./labour/SubcontractorDirectory";

/**
 * LT-Estimate-UX-01 Section 6 — Labour Rates settings.
 *
 * Two rates per trade:
 *   • CHARGE rate — what we price at, appears in every client-facing
 *     export. Public.
 *   • PAY rate    — what we actually pay the contractor. INTERNAL ONLY,
 *     guarded via `client_safe_labour_rate()` server-side.
 *
 * Derived: labour buffer = charge − pay, per trade. Row-level warnings:
 *   • Pay >= Charge  → RED "no buffer / negative buffer" (loud).
 *   • Pay very close (< 5% headroom) → AMBER advisory.
 *
 * Last-updated shown per column (charge_rate_updated_at,
 * pay_rate_updated_at). Falls back to `updated_at` for legacy docs.
 */
export default function LabourRates() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const rawTab = params.get("tab");
  const activeTab = rawTab === "settings" || rawTab === "directory" ? rawTab : "list";
  const setTab = (t) => setParams(t === "list" ? {} : { tab: t });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/labour-rates", { params: { q: q || undefined } })
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Failed to load labour rates"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, activeTab]); // eslint-disable-line

  const bufferSummary = useMemo(() => {
    let noBuffer = 0;
    let tight = 0;
    for (const r of items) {
      const charge = Number(r.hourly_rate || 0);
      const pay    = Number(r.actual_hourly_rate || 0);
      if (charge > 0.005 && pay > 0.005) {
        if (pay >= charge - 0.005) noBuffer += 1;
        else if ((charge - pay) / charge < 0.05) tight += 1;
      }
    }
    return { noBuffer, tight };
  }, [items]);

  return (
    <div className="app-container py-8">
      <PageHeader
        title="Labour Rates"
        subtitle={activeTab === "directory"
          ? "Subcontractor directory — who you get quotes from, by trade. Feeds the Subby Quotes tab on every tender."
          : "Charge rate appears in client-facing tenders. Pay rate is internal only and drives the labour buffer."}
        actions={activeTab === "list" ? (
          <PrimaryButton icon={Plus} onClick={() => navigate("/labour-rates/new")}>Add labour rate</PrimaryButton>
        ) : null}
      />

      {/* Same shape as Materials: the library, then its Settings. The same
          Settings panel also sits under Settings → Library settings. */}
      <div className="mb-4 flex items-center gap-1 border-b border-line" data-testid="labour-rates-tabbar">
        <button type="button" onClick={() => setTab("list")}
          className={"px-4 py-2 -mb-px border-b-2 " + (activeTab === "list" ? "border-accent text-ink font-medium" : "border-transparent text-ink-3 hover:text-ink")}
          style={{ fontSize: 13 }} data-testid="labour-rates-tab-list">
          Labour Rates
        </button>
        <button type="button" onClick={() => setTab("directory")}
          className={"px-4 py-2 -mb-px border-b-2 inline-flex items-center gap-1.5 " + (activeTab === "directory" ? "border-accent text-ink font-medium" : "border-transparent text-ink-3 hover:text-ink")}
          style={{ fontSize: 13 }} data-testid="labour-rates-tab-directory">
          <Users size={13} />
          Directory
        </button>
        <button type="button" onClick={() => setTab("settings")}
          className={"px-4 py-2 -mb-px border-b-2 inline-flex items-center gap-1.5 " + (activeTab === "settings" ? "border-accent text-ink font-medium" : "border-transparent text-ink-3 hover:text-ink")}
          style={{ fontSize: 13 }} data-testid="labour-rates-tab-settings">
          <SettingsIcon size={13} />
          Settings
        </button>
      </div>

      {activeTab === "directory" ? (
        <SubcontractorDirectory />
      ) : activeTab === "settings" ? (
        <div data-testid="labour-rates-settings">
          <div className="mb-3 text-ink-4" style={{ fontSize: 11.5 }}>
            Also under <Link to="/settings?tab=library_labour" className="underline hover:text-ink">Settings → Library settings → Labour Rates</Link> — edit from either place.
          </div>
          <LabourRatesBulkTab />
        </div>
      ) : (
      <>
      {/* LT-Estimate-UX-01 Section 6 — loud banner when any trade has no
          buffer left. Placed at the top so it can't be missed. */}
      {bufferSummary.noBuffer > 0 && (
        <div
          data-testid="labour-rates-no-buffer-banner"
          className="mb-4 rounded-md border px-4 py-3 flex items-start gap-3"
          style={{ background: "#FBECEC", borderColor: "#E9C0C0", color: "#7A1F1F" }}
        >
          <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>
              {bufferSummary.noBuffer} trade{bufferSummary.noBuffer === 1 ? " has" : "s have"} pay rate at or above the charge rate.
            </div>
            <div style={{ fontSize: 12, marginTop: 2 }}>
              A pay rate creeping up to meet the charge rate silently destroys the labour buffer.
              Update either the charge rate or the pay rate below.
            </div>
          </div>
        </div>
      )}
      {bufferSummary.tight > 0 && (
        <div
          data-testid="labour-rates-tight-buffer-banner"
          className="mb-4 rounded-md border px-4 py-3 flex items-start gap-3"
          style={{ background: "#FEF3E2", borderColor: "#F2DFB8", color: "#7A4C0A" }}
        >
          <TrendingDown size={16} className="flex-shrink-0 mt-0.5" />
          <div style={{ fontSize: 12.5 }}>
            {bufferSummary.tight} trade{bufferSummary.tight === 1 ? " has" : "s have"} a tight buffer (under 5% headroom). Review below.
          </div>
        </div>
      )}

      <div className="flex items-center mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-4" />
          <input
            type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by trade…" spellCheck="true"
            data-testid="labour-rates-search"
            className="w-full h-9 pl-9 pr-3 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
            style={{ fontSize: 13 }}
          />
        </div>
      </div>

      <Card>
        {loading ? (
          <div className="py-12 text-center text-ink-3" style={{ fontSize: 12 }}>Loading…</div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={HardHat}
            title="No labour rates yet"
            hint="Add a trade with both a charge rate (client-facing) and a pay rate (internal)."
            action={<PrimaryButton icon={Plus} onClick={() => navigate("/labour-rates/new")}>Add labour rate</PrimaryButton>}
          />
        ) : (
          <table className="w-full text-left" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
            <thead>
              <tr>
                <th rowSpan={2} className="col-head px-4 py-3" style={{ borderBottom: "1px solid #E2DDD2" }}>Trade</th>
                <th rowSpan={2} className="col-head px-4 py-3" style={{ width: 90, borderBottom: "1px solid #E2DDD2" }}>Code</th>
                <th colSpan={2} className="text-center"
                    style={{ background: "#EEF3EE", color: "#3A5A47", fontSize: 9.5, letterSpacing: "0.08em", fontWeight: 700, textTransform: "uppercase", padding: "6px 8px" }}>
                  Charge rate (client-facing)
                </th>
                <th colSpan={2} className="text-center"
                    style={{ background: "#F0EEE6", color: "#7A6238", fontSize: 9.5, letterSpacing: "0.08em", fontWeight: 700, textTransform: "uppercase", padding: "6px 8px" }}>
                  Pay rate (internal) <Lock size={9} className="inline ml-1 -mt-0.5" />
                </th>
                <th rowSpan={2} className="col-head text-right px-4 py-3" style={{ width: 110, borderBottom: "1px solid #E2DDD2" }}>Buffer / hr</th>
                <th rowSpan={2} style={{ width: 30, borderBottom: "1px solid #E2DDD2" }} />
              </tr>
              <tr>
                <th className="col-head text-right" style={{ width: 100, padding: "8px", borderBottom: "1px solid #E2DDD2", background: "#EEF3EE55" }}>£/hr</th>
                <th className="col-head text-right" style={{ width: 100, padding: "8px", borderBottom: "1px solid #E2DDD2", background: "#EEF3EE55" }}>£/day</th>
                <th className="col-head text-right" style={{ width: 100, padding: "8px", borderBottom: "1px solid #E2DDD2", background: "#F0EEE655" }}>£/hr</th>
                <th className="col-head text-right" style={{ width: 100, padding: "8px", borderBottom: "1px solid #E2DDD2", background: "#F0EEE655" }}>£/day</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r, i) => {
                const charge = Number(r.hourly_rate || 0);
                const pay    = Number(r.actual_hourly_rate || 0);
                const bufferHourly = charge - pay;
                const noBuffer = charge > 0.005 && pay > 0.005 && pay >= charge - 0.005;
                const tight = !noBuffer && charge > 0.005 && pay > 0.005 && (charge - pay) / charge < 0.05;
                const bufferColor = noBuffer ? "#B03A2E" : (tight ? "#B45309" : "#3A5A47");
                const chargeUpdated = r.charge_rate_updated_at || r.updated_at;
                const payUpdated = r.pay_rate_updated_at || r.updated_at;
                return (
                  <tr key={r.id}
                    onClick={() => navigate(`/labour-rates/${r.id}`)}
                    data-testid={`labour-rate-row-${r.id}`}
                    className={["group cursor-pointer hover:bg-surface-2/60",
                      i !== items.length - 1 ? "border-b border-line-soft" : ""].join(" ")}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-ink" style={{ fontSize: 13, fontWeight: 500 }}>{r.trade_name}</span>
                        {noBuffer && (
                          <span
                            title="Pay rate is at or above the charge rate — no buffer"
                            data-testid={`labour-rate-no-buffer-${r.id}`}
                            className="inline-flex items-center gap-1 rounded font-mono"
                            style={{ background: "#FBECEC", color: "#7A1F1F", border: "1px solid #E9C0C0", padding: "1px 5px", fontSize: 9, fontWeight: 700, letterSpacing: "0.06em" }}
                          >
                            NO BUFFER
                          </span>
                        )}
                        {tight && (
                          <span
                            title="Buffer is under 5% of the charge rate"
                            data-testid={`labour-rate-tight-buffer-${r.id}`}
                            className="inline-flex items-center gap-1 rounded font-mono"
                            style={{ background: "#FEF3E2", color: "#7A4C0A", border: "1px solid #F2DFB8", padding: "1px 5px", fontSize: 9, fontWeight: 700, letterSpacing: "0.06em" }}
                          >
                            TIGHT
                          </span>
                        )}
                      </div>
                      <div className="text-ink-3 mt-0.5" style={{ fontSize: 10.5 }}>
                        Charge {ukDate(chargeUpdated) || "—"} · Pay {ukDate(payUpdated) || "—"}
                      </div>
                    </td>
                    <td className="px-4 py-3"><span className="font-mono text-ink-3" style={{ fontSize: 11.5 }}>{r.trade_code || "—"}</span></td>
                    <td className="text-right font-mono tnum text-ink py-3" style={{ fontSize: 12.5, paddingRight: 12, background: "#EEF3EE26" }}>{gbp(charge)}</td>
                    <td className="text-right font-mono tnum text-ink py-3" style={{ fontSize: 12.5, paddingRight: 12, background: "#EEF3EE26" }}>{gbp(r.day_rate)}</td>
                    <td className="text-right font-mono tnum text-ink-2 py-3" style={{ fontSize: 12.5, paddingRight: 12, background: "#F0EEE626" }}>{gbp(pay)}</td>
                    <td className="text-right font-mono tnum text-ink-2 py-3" style={{ fontSize: 12.5, paddingRight: 12, background: "#F0EEE626" }}>{gbp(r.actual_day_rate)}</td>
                    <td className="text-right font-mono tnum py-3" style={{ fontSize: 12.5, paddingRight: 16, color: bufferColor, fontWeight: 600 }}
                      data-testid={`labour-rate-buffer-${r.id}`}>
                      {gbp(bufferHourly)}
                    </td>
                    <td className="pr-4 py-3 text-right"><ChevronRight size={14} className="text-ink-4 group-hover:text-ink-2" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
      </>
      )}
    </div>
  );
}
